import type { BotWidgetConfig } from './types'

export const DEFAULT_ICON = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
</svg>`

export const DEFAULT_CLOSE_ICON = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M18.601 8.39897C18.269 8.06702 17.7309 8.06702 17.3989 8.39897L12 13.7979L6.60099 8.39897C6.26904 8.06702 5.73086 8.06702 5.39891 8.39897C5.06696 8.73091 5.06696 9.2691 5.39891 9.60105L11.3989 15.601C11.7309 15.933 12.269 15.933 12.601 15.601L18.601 9.60105C18.9329 9.2691 18.9329 8.73091 18.601 8.39897Z" fill="white"></path>
</svg>`

export const RACCOON_ICON = `<img src="https://expressirsforms.s3.us-east-1.amazonaws.com/SpanSupport/Logo/RaccoonwithHeadphone.png" alt="Chat Launcher" width="50" height="50">`

// IcoMoon four-pointed-star path (viewBox 0 0 1024 1024) — the "Ask BanditAI"
// launcher pill icon composites two independent copies of this same star, each its
// own standalone <svg> (not two <path>s sharing one <svg>), sized/positioned purely
// via CSS (.bot-launcher-icon-star-large/-small in styles.ts) rather than baked-in
// transform attributes — a large orange star with a smaller blue star overlapping
// its upper-right point. Replaces the plain "✦" text glyph the pill had before.
const SPARKLE_STAR_PATH =
  'M452.138 139.85l47.217-130.975c4.177-11.833 20.998-11.833 25.29 0l47.218 130.975c25.906 71.807 67.323 137.020 121.303 190.999s119.194 95.395 191.003 121.3l130.977 47.217c2.596 0.945 4.837 2.664 6.42 4.927 1.583 2.262 2.433 4.956 2.433 7.717s-0.85 5.457-2.433 7.718c-1.583 2.263-3.825 3.982-6.42 4.927l-130.977 47.217c-71.809 25.906-137.024 67.322-191.003 121.3-53.98 53.979-95.397 119.192-121.303 190.999l-47.218 130.975c-0.945 2.596-2.665 4.837-4.927 6.42s-4.957 2.433-7.718 2.433c-2.761 0-5.457-0.85-7.718-2.433s-3.982-3.825-4.927-6.42l-47.217-130.975c-25.907-71.807-67.324-137.020-121.304-190.999s-119.194-95.395-191.003-121.3l-130.978-47.217c-2.595-0.945-4.836-2.664-6.42-4.927-1.584-2.262-2.433-4.956-2.433-7.718s0.85-5.455 2.433-7.717c1.584-2.263 3.825-3.982 6.42-4.927l130.978-47.217c71.809-25.906 137.024-67.322 191.003-121.3 53.98-53.979 95.397-119.192 121.304-190.999z'

function sparkleStarSvg(sizeClass: string, fill: string): string {
  return `<svg class="bot-launcher-icon-star ${sizeClass}" viewBox="0 0 1024 1024" fill="${fill}" xmlns="http://www.w3.org/2000/svg"><path d="${SPARKLE_STAR_PATH}"></path></svg>`
}

export const SPARKLE_ICON_LARGE = sparkleStarSvg('bot-launcher-icon-star-large', 'rgb(242, 109, 33)')
export const SPARKLE_ICON_SMALL = sparkleStarSvg('bot-launcher-icon-star-small', 'rgb(48, 151, 243)')

// Greeting card's close button — a stroked SVG rather than the "×" glyph, which
// renders thin and blurry and shifts with the host page's font.
export const CLOSE_ICON =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" aria-hidden="true">' +
  '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>' +
  '</svg>'

// Arrow for the greeting card's "Or type your question" send button.
export const SEND_ARROW_ICON =
  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>' +
  '</svg>'

// 2×3 dot grip shown beside the launcher when config.isEnableLauncherDrag is on.
export const DRAG_GRIP_ICON =
  '<svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor" aria-hidden="true">' +
  '<circle cx="2" cy="2" r="1.5"/><circle cx="8" cy="2" r="1.5"/>' +
  '<circle cx="2" cy="8" r="1.5"/><circle cx="8" cy="8" r="1.5"/>' +
  '<circle cx="2" cy="14" r="1.5"/><circle cx="8" cy="14" r="1.5"/>' +
  '</svg>'

// TaxBandits-non-CPA, the home site, and span-control tenants get the
// branded raccoon launcher; everyone else gets the plain speech-bubble icon
// (or their own customIcon, which always wins).
function wantsRaccoonIcon(config: BotWidgetConfig): boolean {
  const isTaxBanditsNonCpa = (config.product?.toLowerCase()?.includes('taxbandits') ?? false) && !config.isCPA
  return isTaxBanditsNonCpa || Boolean(config.isHomeSite) || Boolean(config.isSpanControl)
}

export function isDefaultIcon(config: BotWidgetConfig): boolean {
  return !config.customIcon && !wantsRaccoonIcon(config)
}

export function launcherIcon(config: BotWidgetConfig, fallbackToDefault = true): string | null {
  if (config.customIcon) return config.customIcon
  if (wantsRaccoonIcon(config)) return RACCOON_ICON
  return fallbackToDefault ? DEFAULT_ICON : null
}
